import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CashRequestDetail } from "../schema/cash-requests.schema";
import { CashRequestStatus } from "@/constants/entity-status";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CashRequestAmountAndActions } from "@/components/common/section-components/requests/cash/CashRequestAmountAndActions";
import { CashRequestMainDataCard } from "@/components/common/section-components/card/CashRequestMainDataCard";
import { CashRequestDetailLegend } from "@/components/common/section-components/requests/cash/CashRequestDetailLegend";
import { CashRequestMessageList } from "@/components/common/section-components/requests/cash/CashRequestMessageList";
import { useAuth } from "@/hooks/useAuth";
import { RequestSectionHeaderTitle } from "@/components/common/section-components/requests/RequestSectionHeaderTitle";
import { RequestMovementNumber } from "@/components/common/section-components/requests/RequestMovementNumber";

import { useRoles } from "@/hooks/useRoles";
import {
  cashRequestSendInfoHandler,
  fetchCashRequestHandler,
  handleAcceptRequestHander,
  rejectCashRequestHandler,
  updateCashRequestHandler,
} from "./handler-functions";
import { validateMinCurrencyAmount } from "@/utils/request-validations";

export const CashRequestDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.CASH_REQUESTS);
  };

  const [loadingRequest, setLoadingRequest] = useState<boolean>(false);

  const [request, setRequest] = useState<CashRequestDetail | null>();

  const fetchRequest = async (showLoad: boolean = true) => {
    await fetchCashRequestHandler({
      fetchPermissions,
      id,
      navigate,
      setLoadingRequest,
      setRequest,
      showLoad,
    });
  };

  useEffect(() => {
    if (isActive && id && user) fetchRequest();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);

  const handleEditAmount = async () => {
    updateCashRequestHandler({
      setLoadingEditAmount,
      navigate,
      request,
      watchedValues,
      errors,
      fetchRequest,
      fetchPermissions,
      setIsEditingAmount,
    });
  };

  const radioItems = [
    { label: "Usted", color: AppColors.BLACK_TEXT_SECONDARY },
    { label: "Usuario", color: AppColors.WHITE_BORDER_1 },
  ];

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    resolver: zodResolver(
      z.object({
        request_amount: z.any().optional(),
      }),
    ),
  });

  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const [loadingSendInfo, setLoadingSendInfo] = useState<boolean>(false);
  const [loadingRejectRequest, setLoadingRejectRequest] =
    useState<boolean>(false);
  const [loadingAcceptRequest, setLoadingAcceptRequest] =
    useState<boolean>(false);

  const watchedValues = useWatch({
    control,
  });

  const { fetchPermissions } = useRoles();

  const handleSendInfo = async (event?: FieldValues) => {
    cashRequestSendInfoHandler({
      setLoadingSendInfo,
      navigate,
      request,
      event,
      fetchRequest,
      fetchPermissions,
      setOpenInfoModal,
    });
  };

  const handleRejectRequest = async (event?: FieldValues) => {
    rejectCashRequestHandler({
      setLoadingRejectRequest,
      navigate,
      request,
      event,
      fetchRequest,
      fetchPermissions,
      setOpenRejectModal,
    });
  };

  const handleAcceptRequest = async (status: CashRequestStatus) => {
    handleAcceptRequestHander({
      setLoadingAcceptRequest,
      navigate,
      request,
      status,
      fetchRequest,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (request) {
      setValue("request_amount", request?.montoSolicitado);
    }
  }, [request]);

  useEffect(() => {
    validateMinCurrencyAmount({
      clearErrors,
      setError,
      watchedAmount: watchedValues?.request_amount,
      min: 1,
      fieldName: "request_amount",
    });
  }, [watchedValues?.request_amount, errors]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={<RequestSectionHeaderTitle request={request} type="Efectivo" />}
        onBack={handleBack}
        backButton
        loading={
          loadingRequest ||
          loadingRejectRequest ||
          loadingSendInfo ||
          loadingAcceptRequest
        }
      />
      <RequestMovementNumber request={request} />
      {!loadingRequest && (
        <>
          <CashRequestAmountAndActions
            control={control}
            isEditingAmount={isEditingAmount}
            loadingEditAmount={loadingEditAmount}
            loadingRejectRequest={loadingRejectRequest}
            loadingAcceptRequest={loadingAcceptRequest}
            loadingRequest={loadingRequest}
            loadingSendInfo={loadingSendInfo}
            request={request}
            errors={errors}
            setOpenInfoModal={setOpenInfoModal}
            setOpenRejectModal={setOpenRejectModal}
            handleAcceptRequest={handleAcceptRequest}
            handleEditAmount={handleEditAmount}
            setValue={setValue}
            setIsEditingAmount={setIsEditingAmount}
            clearErrors={clearErrors}
          />

          <CashRequestMainDataCard request={request} />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <CashRequestDetailLegend radioItems={radioItems} />

            <CashRequestMessageList request={request} />
          </Flex>
        </>
      )}

      {request?.id && (
        <Suspense>
          <NeedInfoModalDynamic
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
          />
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectRequest}
            loadingAccept={loadingRejectRequest}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const NeedInfoModalDynamic = lazy(() =>
  import("@/components/modal/NeedInfoModal").then((el) => ({
    default: el["NeedInfoModal"],
  })),
);

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);
